"use client"

import { useState, useEffect, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { toast } from "react-toastify"
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Grid,
  Divider,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  InputAdornment,
  styled,
} from "@mui/material"
import {
  Send as SendIcon,
  Delete as DeleteIcon,
  Link as LinkIcon,
  Call as CallIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  UploadFile as UploadFileIcon,
} from "@mui/icons-material"
import DashboardLayout from "../components/DashboardLayout"
import { createCampaign, resetCampaignState } from "../slices/campaignSlice"
import { API_URL, MAX_CAMPAIGN_MESSAGE_LENGTH } from "../constants"

const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
  "&.Mui-selected": {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
}))

const ButtonCampaignPage = () => {
  const dispatch = useDispatch()
  const { userInfo } = useSelector((state) => state.auth)
  const { loading, error, success, campaign } = useSelector((state) => state.campaigns)

  // Campaign config from the store
  const [campaignConfig, setCampaignConfig] = useState({
    maxMedia: 1,
    mediaSize: "5 MB",
  })
  const numbersStatsRef = useRef(null)
  // Form state
  const [title, setTitle] = useState("")
  const [message, setMessage] = useState("")
  const [numbers, setNumbers] = useState("")
  const [numberFormat, setNumberFormat] = useState("manual")
  const [selectedFile, setSelectedFile] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const [media, setMedia] = useState([])

  // Button state
  const [buttons, setButtons] = useState([
    { type: "link", text: "Visit Now", value: "" },
    { type: "call", text: "Call Now", value: "" },
  ])

  // Refs for file inputs
  const mediaInputRef = useRef(null)
  const fileInputRef = useRef(null)

  // Fetch campaign config
  useEffect(() => {
    const fetchCampaignConfig = async () => {
      try {
        const response = await fetch(`${API_URL}/api/campaign-config`, {
          headers: {
            Authorization: `Bearer ${userInfo.token}`,
          },
        })
        const data = await response.json()

        if (data && data.length > 0) {
          const configObj = {}
          data.forEach((item) => {
            if (item.name === "Number of Images") configObj.maxMedia = Number.parseInt(item.value)
            if (item.name === "Image Size") configObj.mediaSize = item.value
          })
          setCampaignConfig(configObj)
        }
      } catch (error) {
        console.error("Failed to fetch campaign config:", error)
      }
    }

    fetchCampaignConfig()
  }, [userInfo.token])

  // Handle success and error states
  useEffect(() => {
    if (success) {
      toast.success("Button campaign created successfully")
      // Reset form
      setTitle("")
      setMessage("")
      setNumbers("")
      setMedia([])
      setButtons([
        { type: "link", text: "Visit Now", value: "" },
        { type: "call", text: "Call Now", value: "" },
      ])
      dispatch(resetCampaignState())
    }
  }, [success, dispatch])

  useEffect(() => {
    if (error) {
      toast.error(error)
      dispatch(resetCampaignState())
    }
  }, [error, dispatch])

  const handleNumberFormatChange = (event, newFormat) => {
    if (newFormat !== null) {
      setNumberFormat(newFormat)
      // Clear numbers when switching to CSV or Excel
      if (newFormat !== "manual") {
        setNumbers("")
      }
    }
  }

  const handleFileUpload = async (file) => {
    setIsUploading(true)
    try {
      const formData = new FormData()
      formData.append("file", file)

      const response = await fetch(`${API_URL}/api/upload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
        },
        body: formData,
      })

      if (!response.ok) {
        throw new Error(`Failed to upload file: ${response.statusText}`)
      }

      const data = await response.json()

      if (data.success) {
        let type = "image"
        if (file.type.includes("video")) {
          type = "video"
        } else if (file.type.includes("pdf")) {
          type = "pdf"
        }

        const fileObj = {
          name: file.name,
          url: data.filePath,
          type,
        }

        setMedia([fileObj]) // Only one media allowed for button campaigns
        toast.success(`Media uploaded successfully`)
      } else {
        throw new Error(data.message || "Failed to upload file")
      }
    } catch (error) {
      toast.error(`Failed to upload media: ${error.message}`)
    } finally {
      setIsUploading(false)
    }
  }

  const handleMediaUpload = (e) => {
    const file = e.target.files[0]
    if (!file) return

    if (media.length >= campaignConfig.maxMedia) {
      toast.error(`You can only upload ${campaignConfig.maxMedia} media file`)
      return
    }

    handleFileUpload(file)
  }

  const handleRemoveMedia = () => {
    setMedia([])
  }

  const handleButtonChange = (index, field, value) => {
    const updatedButtons = [...buttons]
    updatedButtons[index] = { ...updatedButtons[index], [field]: value }
    setButtons(updatedButtons)
  }

  const handleFileSelect = (event) => {
    const file = event.target.files[0]
    setSelectedFile(file)

    if (file) {
      // Process the file content here
      const reader = new FileReader()
      reader.onload = (e) => {
        const fileContent = e.target.result
        // Handle CSV or Excel content
        setNumbers(fileContent)
      }
      reader.onerror = (e) => {
        toast.error("Error reading file")
      }

      reader.readAsText(file)
    }
  }

  const handleSubmit = () => {

    if (numberStats.invalid > 0 || numberStats.duplicate) {
      toast.error("Please remove all invalid or duplicate numbers before submitting");
      numbersStatsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      })
      return
    }

    if (!title.trim()) {
      toast.error("Please enter a campaign title")
      return
    }

    if (!message.trim()) {
      toast.error("Please enter a message")
      return
    }

    if (message.length > MAX_CAMPAIGN_MESSAGE_LENGTH) {
      toast.error(`Campaign message cannot exceed ${MAX_CAMPAIGN_MESSAGE_LENGTH} characters`)
      return
    }

    if (!numbers.trim()) {
      toast.error("Please enter at least one phone number")
      return
    }

    // Validate buttons
    const linkButton = buttons.find((btn) => btn.type === "link")
    const callButton = buttons.find((btn) => btn.type === "call")
    // if (( !linkButton.value.trim()) &&  !callButton.value.trim())  {
    //   toast.error("Please enter atleast one button")
    //   return
    // }

   

    // if (callButton && (!callButton.value.trim())) {
    //   toast.error("Please enter a valid phone number for the call button")
    //   return
    // }

    // Validate link format
    if (linkButton && !linkButton.value.startsWith("http") && linkButton.value.trim()) {
      toast.error("Link URL must start with http:// or https://")
      return
    }

    const campaignData = {
      title,
      message,
      campaignType: "button",
      campaignOption: "domestic", // Button campaigns are domestic only
      numbers: numbers.trim(),
      country: "India",
      countryCode: "+91",
      media: media.map((m) => ({ type: m.type, url: m.url, name: m.name })),
      buttons: buttons.filter((btn) => btn.value && btn.value.trim()),
    }

    dispatch(createCampaign(campaignData))
  }

  // Helper functions for number processing
  const parseNumbers = (numbersStr) => {
    return numbersStr
      .split(/\r?\n/)
      .map((n) => n.trim())
      .filter((n) => n.length > 0)
  }

  const isValidNumber = (num) => {
    // India only: 10 digits
    const digitsOnly = num.replace(/\D/g, "")
    return /^\d{10}$/.test(digitsOnly)
  }

  const numberStats = (() => {
    const arr = parseNumbers(numbers)
    const seen = new Set()
    const duplicates = new Set()
    const invalids = []
    const valids = []

    arr.forEach((num) => {
      if (seen.has(num)) {
        duplicates.add(num)
      } else {
        seen.add(num)
      }
      if (isValidNumber(num)) {
        valids.push(num)
      } else {
        invalids.push(num)
      }
    })

    return {
      total: arr.length,
      valid: valids.length,
      invalid: invalids.length,
      duplicate: duplicates.size,
      duplicates: Array.from(duplicates),
      invalidNumbers: invalids,
      validNumbers: valids,
      allNumbers: arr,
    }
  })()

  const handleRemoveDuplicates = () => {
    const uniqueNumbers = numberStats.allNumbers.filter(
      (num, idx, arr) => arr.indexOf(num) === idx
    )
    setNumbers(uniqueNumbers.join("\n"))
    toast.success("Duplicate numbers removed")
  }

  const handleRemoveInvalids = () => {
    setNumbers(numberStats.validNumbers.join("\n"))
    toast.success("Invalid numbers removed")
  }

  return (
    <DashboardLayout>
      <Box sx={{ maxWidth: 1200, mx: "auto" }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Button Campaign
        </Typography>

        <Paper sx={{ p: 3, mb: 4 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Button Campaign
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }} ref={numbersStatsRef}>
                <Chip label={`${numberStats.total} Total`} color="default" />
                <Chip label={`${numberStats.valid} Valid`} color="success" />
                <Chip label={`${numberStats.invalid} Invalid`} color="error" />
                <Chip label={`${numberStats.duplicate} Duplicate`} color="warning" />
                {numberStats.duplicate > 0 && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    sx={{ ml: 1 }}
                    onClick={handleRemoveDuplicates}
                  >
                    Remove Duplicates
                  </Button>
                )}
                {numberStats.invalid > 0 && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    sx={{ ml: 1 }}
                    onClick={handleRemoveInvalids}
                  >
                    Remove Invalid
                  </Button>
                )}
              </Box>
            </Box>
          </Box>

          <Grid container spacing={3}>

            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Campaign Title
              </Typography>
              <TextField
                fullWidth
                placeholder="Campaign Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Message
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={6}
                placeholder="Type your message here"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                inputProps={{ maxLength: MAX_CAMPAIGN_MESSAGE_LENGTH }}
                required
              />
              <Typography variant="caption" color="text.secondary">
                Message Character Count: {message.length}/{MAX_CAMPAIGN_MESSAGE_LENGTH}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Buttons <span style={{ color: "red" }}>(iPhone May Not be Supported)</span>
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <LinkIcon sx={{ mr: 1, color: "primary.main" }} />
                    <Typography variant="subtitle2">Link</Typography>
                  </Box>
                  <TextField
                    fullWidth
                    placeholder="Visit Now"
                    value={buttons[0].text}
                    onChange={(e) => handleButtonChange(0, "text", e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    placeholder="https://example.com"
                    value={buttons[0].value}
                    onChange={(e) => handleButtonChange(0, "value", e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LinkIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <CallIcon sx={{ mr: 1, color: "primary.main" }} />
                    <Typography variant="subtitle2">Call</Typography>
                  </Box>
                  <TextField
                    fullWidth
                    placeholder="Call Now"
                    value={buttons[1].text}
                    onChange={(e) => handleButtonChange(1, "text", e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    placeholder="10-15 Digit Number"
                    value={buttons[1].value}
                    onChange={(e) => handleButtonChange(1, "value", e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CallIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Max 1 Media
              </Typography>
              <input
                type="file"
                accept="image/*,video/*,.pdf"
                onChange={handleMediaUpload}
                ref={mediaInputRef}
                style={{ display: "none" }}
                disabled={isUploading || media.length >= campaignConfig.maxMedia}
              />
              <Button
                variant="outlined"
                onClick={() => mediaInputRef.current.click()}
                disabled={isUploading || media.length >= campaignConfig.maxMedia}
                fullWidth
              >
                Select Media(Max {campaignConfig.mediaSize})
              </Button>

              <TableContainer sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>SR. NO.</TableCell>
                      <TableCell>NAME</TableCell>
                      <TableCell>ACTION</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {media.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          No media uploaded
                        </TableCell>
                      </TableRow>
                    ) : (
                      media.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{item.name}</TableCell>
                          <TableCell>
                            <IconButton size="small" color="error" onClick={handleRemoveMedia}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Choose Number Format
              </Typography>
              <ToggleButtonGroup
                value={numberFormat}
                exclusive
                onChange={handleNumberFormatChange}
                aria-label="number format"
                sx={{ mb: 3 }}
              >
                <StyledToggleButton value="manual" aria-label="manual">
                  Manual
                </StyledToggleButton>
                <StyledToggleButton value="csv" aria-label="csv">
                  CSV Numbers
                </StyledToggleButton>
                <StyledToggleButton value="excel" aria-label="excel">
                  Excel Numbers
                </StyledToggleButton>
              </ToggleButtonGroup>

              {numberFormat !== "manual" && (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", mb: 2 }}>
                  <Button
                    variant="contained"
                    component="label"
                    startIcon={<UploadFileIcon />}
                    sx={{ mb: 1, bgcolor: "#3f51b5", color: "white", "&:hover": { bgcolor: "#303f9f" } }}
                  >
                    Choose {numberFormat.toUpperCase()} File
                    <input
                      type="file"
                      hidden
                      accept={numberFormat === "csv" ? ".csv" : ".xlsx, .xls"}
                      onChange={handleFileSelect}
                      ref={fileInputRef}
                    />
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    Note: Only {numberFormat === "csv" ? ".csv" : ".xlsx"} is acceptable.
                    <br />
                    Note: There should be only one column and the title of the column should be "Numbers".
                    <br />
                    Note: Any other format will not be accepted.
                  </Typography>
                  <Box sx={{ mt: 1, display: "flex" }}>
                    {numberFormat === "csv" ? (
                      <Button
                        href="/sample.csv"
                        download="sample.csv"
                        variant="text"
                        sx={{ textTransform: "none", color: "primary.main" }}
                      >
                        Download Sample CSV
                      </Button>
                    ) : (
                      <Button
                        href="/sample.xlsx"
                        download="sample.xlsx"
                        variant="text"
                        sx={{ textTransform: "none", color: "primary.main" }}
                      >
                        Download Sample Excel
                      </Button>
                    )}
                  </Box>
                </Box>
              )}
            </Grid>

            {numberFormat === "manual" && (
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Mobile Numbers
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={10}
                  placeholder="Enter phone numbers, one per line"
                  value={numbers}
                  onChange={(e) => setNumbers(e.target.value)}
                  onBlur={handleRemoveDuplicates}
                  required
                />
                <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                  Note: Each number should be in a new line.
                </Typography>
              </Grid>
            )}

            <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                onClick={handleSubmit}
                disabled={loading || isUploading}
                sx={{
                  bgcolor: "#008069",
                  "&:hover": { bgcolor: "#128C7E" },
                }}
              >
                {loading ? "Sending..." : "Send Now"}
              </Button>
            </Grid>

            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  bgcolor: "#f8f9fa",
                  position: "relative",
                  overflow: "hidden",
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "4px",
                    bgcolor: "primary.main",
                  }}
                />
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    color: "primary.main",
                    mb: 2,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <InfoIcon sx={{ mr: 1, fontSize: 20 }} />
                  Button Campaign Guidelines
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {[
                    "Button Campaign may take 30-240 minutes to deliver",
                    "Only 01 image or 01 video or 01 PDF allowed per campaign",
                    "Button Campaigns are available for INDIA only",
                    "Separate credits needed for Button Campaigns",
                    "Link should be added with HTTP or HTTPS",
                    "+91 need not be added for call button",
                    "No Elections Campaigns allowed for Button Campaign",
                  ].map((note, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        backgroundColor: "rgba(37, 211, 102, 0.05)",
                        p: 1.5,
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "rgba(37, 211, 102, 0.2)",
                      }}
                    >
                      <CheckCircleIcon
                        fontSize="small"
                        color="primary"
                        sx={{
                          mr: 1.5,
                          mt: 0.2,
                          color: "#008069",
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: "text.primary",
                          lineHeight: 1.4,
                        }}
                      >
                        {note}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>

          </Grid>
        </Paper>
      </Box>
    </DashboardLayout>
  )
}

export default ButtonCampaignPage
